import React, { useMemo, useState, useCallback } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    Divider,
    Stack,
} from '@mui/material';
import {
    Add as AddIcon,
    AutoAwesome as AutoAwesomeIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Visibility as VisibilityIcon,
    RestartAlt as RestartAltIcon,
    ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePaginatedValuations, useValuations } from '../../../hooks/administration/useValuations';
import { useProjects } from '../../../hooks/useProjects';
import { useCurrencieTypes } from '../../../hooks/accounting/useCurrencieTypes';
import { useTaxesTypes } from '../../../hooks/accounting/useTaxesTypes';
import { useAuthContext } from '../../../context/AuthProvider';

// Estos dos componentes los construiremos después
import ValuationForm from './ValuationForm';
import ValuationGenerateForm from './ValuationGenerateForm';
import formatDate from './utils/formatDate';

const STATE_CONFIG = {
    CREADA: { label: 'CREADA', bg: '#90a4ae', color: '#fff' },
    EN_PROCESO: { label: 'EN PROCESO', bg: '#1976d2', color: '#fff' },
    EN_REVISION: { label: 'EN REVISIÓN', bg: '#f9a825', color: '#000' },
    APROBADA: { label: 'APROBADA', bg: '#2e7d32', color: '#fff' },
    FACTURADA: { label: 'FACTURADA', bg: '#6a1b9a', color: '#fff' },
    COBRADA: { label: 'COBRADA', bg: '#00695c', color: '#fff' },
    CANCELADA: { label: 'CANCELADA', bg: '#d32f2f', color: '#fff' },
};

/*const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(`${value}T00:00:00`);
    return new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
};*/

const formatCurrency = (value, symbol = 'S/') => {
    const number = Number(value || 0);
    return `${symbol} ${number.toFixed(2)}`;
};

const ValuationsDashboard = ({ taxes = [], }) => {
    const navigate = useNavigate();
    const { user } = useAuthContext();
    const { projects, isLoading: isLoadingProjects, error: errorProjects } = useProjects();
    const { currencieTypes, isLoading: isLoadingCurrencies, error: errorCurrencies } = useCurrencieTypes();
    const { taxesTypes, isLoading:isLoadingTaxes, error: errorTaxes } = useTaxesTypes();


    const {
        deleteMutation,
        recalculateMutation,
    } = useValuations();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [search, setSearch] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [projectFilter, setProjectFilter] = useState('');
    const [fecIni, setFecIni] = useState('');
    const [fecFin, setFecFin] = useState('');

    const [openManualForm, setOpenManualForm] = useState(false);
    const [openGenerateForm, setOpenGenerateForm] = useState(false);
    const [editValuation, setEditValuation] = useState(null);

    const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
    const [selectedValuation, setSelectedValuation] = useState(null);

    const [alert, setAlert] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const showAlert = useCallback((message, severity = 'success') => {
        setAlert({
            open: true,
            message,
            severity,
        });
    }, []);

    const {
        valuations,
        total,
        isLoading,
        isFetching,
        error,
    } = usePaginatedValuations({
        page: page + 1,
        limit: rowsPerPage,
        search,
        state: stateFilter,
        proID: projectFilter,
        fecIni,
        fecFin,
    });

    const summaryCards = useMemo(() => {
        const source = Array.isArray(valuations) ? valuations : [];

        const totalGeneral = source.reduce((sum, row) => sum + Number(row.valcabtot || 0), 0);
        const totalFacturadas = source
            .filter((row) => row.valcabest === 'FACTURADA')
            .reduce((sum, row) => sum + Number(row.valcabtot || 0), 0);
        const totalCobradas = source
            .filter((row) => row.valcabest === 'COBRADA')
            .reduce((sum, row) => sum + Number(row.valcabtot || 0), 0);

        return {
            cantidad: source.length,
            totalGeneral,
            totalFacturadas,
            totalCobradas,
        };
    }, [valuations]);

    const handleCloseAlert = () => {
        setAlert((prev) => ({ ...prev, open: false }));
    };

    const handleOpenDelete = (valuation) => {
        setSelectedValuation(valuation);
        setOpenConfirmDelete(true);
    };

    const handleConfirmDelete = () => {
        if (!selectedValuation) return;

        deleteMutation.mutate(selectedValuation.valcabid, {
            onSuccess: () => {
                showAlert('Valorización eliminada correctamente.');
                setOpenConfirmDelete(false);
                setSelectedValuation(null);
            },
            onError: (err) => {
                showAlert(err?.message || 'Error al eliminar la valorización.', 'error');
            },
        });
    };

    const handleRecalculate = (valuation) => {
        recalculateMutation.mutate(valuation.valcabid, {
            onSuccess: () => {
                showAlert('Totales recalculados correctamente.');
            },
            onError: (err) => {
                showAlert(err?.message || 'Error al recalcular la valorización.', 'error');
            },
        });
    };

    const handleOpenEdit = (valuation) => {
        setEditValuation(valuation);
        setOpenManualForm(true);
    };

    const canDelete = (valuation) =>
        ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest);

    const canEdit = (valuation) =>
        ['CREADA', 'EN_PROCESO'].includes(valuation?.valcabest);

    /*if (isLoading) {
      return (
        <Box
          minHeight="320px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress size={56} />
        </Box>
      );
    }
  
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 3 }}>
          Error al cargar valorizaciones: {error.message}
        </Alert>
      );
    }*/

    return (
        <Paper
            sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: '#fafafa',
            }}
        >
            <Snackbar
                open={alert.open}
                autoHideDuration={5000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={alert.severity} onClose={handleCloseAlert}>
                    {alert.message}
                </Alert>
            </Snackbar>

            {/* Encabezado */}
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                flexDirection={{ xs: 'column', md: 'row' }}
                gap={2}
                mb={3}
            >
                <Box>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                        Gestión de Valorizaciones
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        Administra valorizaciones manuales y automáticas, controla estados y revisa sus totales.
                    </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => {
                            setEditValuation(null);
                            setOpenGenerateForm(true);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Generar automática
                    </Button>

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            setEditValuation(null);
                            setOpenManualForm(true);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Nueva valorización
                    </Button>
                </Stack>
            </Box>

            {/* Resumen superior */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={3}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 2.2, borderRadius: 2.5, height: '100%' }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Valorizaciones visibles
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" mt={1}>
                            {summaryCards.cantidad}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 2.2, borderRadius: 2.5, height: '100%' }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Total visible
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" mt={1}>
                            {formatCurrency(summaryCards.totalGeneral)}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 2.2, borderRadius: 2.5, height: '100%' }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Total facturado
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" mt={1}>
                            {formatCurrency(summaryCards.totalFacturadas)}
                        </Typography>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={3}>
                    <Paper
                        variant="outlined"
                        sx={{ p: 2.2, borderRadius: 2.5, height: '100%' }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Total cobrado
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" mt={1}>
                            {formatCurrency(summaryCards.totalCobradas)}
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Filtros */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderRadius: 2.5,
                    mb: 3,
                    backgroundColor: '#fff',
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold" mb={2}>
                    Filtros
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Buscar"
                            size="small"
                            fullWidth
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            placeholder="ID, descripción o proyecto"
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Estado"
                            size="small"
                            fullWidth
                            select
                            value={stateFilter}
                            onChange={(e) => {
                                setStateFilter(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {Object.entries(STATE_CONFIG).map(([key, config]) => (
                                <MenuItem key={key} value={key}>
                                    {config.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <TextField
                            label="Proyecto"
                            size="small"
                            fullWidth
                            select
                            value={projectFilter}
                            onChange={(e) => {
                                setProjectFilter(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            {projects.map((project) => (
                                <MenuItem key={project.proid} value={project.proid}>
                                    {project.pronom}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Fecha inicio"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fecIni}
                            onChange={(e) => {
                                setFecIni(e.target.value);
                                setPage(0);
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={2}>
                        <TextField
                            label="Fecha fin"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={fecFin}
                            onChange={(e) => {
                                setFecFin(e.target.value);
                                setPage(0);
                            }}
                        />
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button
                        variant="text"
                        onClick={() => {
                            setSearch('');
                            setStateFilter('');
                            setProjectFilter('');
                            setFecIni('');
                            setFecFin('');
                            setPage(0);
                        }}
                        sx={{ textTransform: 'none' }}
                    >
                        Limpiar filtros
                    </Button>
                </Box>
            </Paper>

            {/* Indicador de refresco */}
            {isFetching && (
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                        Actualizando datos...
                    </Typography>
                </Box>
            )}

            {/* Tabla */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 2.5,
                    overflow: 'hidden',
                }}
            >
                <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell><strong>Código</strong></TableCell>
                            <TableCell><strong>Proyecto</strong></TableCell>
                            <TableCell><strong>Moneda</strong></TableCell>
                            <TableCell><strong>Periodo</strong></TableCell>
                            <TableCell align="right"><strong>Subtotal</strong></TableCell>
                            <TableCell align="right"><strong>Impuesto</strong></TableCell>
                            <TableCell align="right"><strong>Total</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell align="center"><strong>Acciones</strong></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {!valuations || valuations.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        gap={1}
                                    >
                                        <ReceiptLongIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                                        <Typography variant="subtitle1" color="text.secondary">
                                            No se encontraron valorizaciones
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            valuations.map((valuation) => {
                                const stateInfo = STATE_CONFIG[valuation.valcabest] || {
                                    label: valuation.valcabest,
                                    bg: '#bdbdbd',
                                    color: '#000',
                                };

                                return (
                                    <TableRow key={valuation.valcabid} hover>
                                        <TableCell>
                                            <Typography fontWeight="bold" color="primary">
                                                VAL-{String(valuation.valcabid).padStart(6, '0')}
                                            </Typography>
                                        </TableCell>

                                        <TableCell sx={{ maxWidth: 220 }}>
                                            <Typography noWrap title={valuation.pronom}>
                                                {valuation.pronom || '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2">
                                                {valuation.moncod || '-'}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(valuation.valcabfecini)} - {formatDate(valuation.valcabfecfin)}
                                            </Typography>
                                        </TableCell>

                                        <TableCell align="right">
                                            {formatCurrency(valuation.valcabsubtotal, valuation.monsim || 'S/')}
                                        </TableCell>

                                        <TableCell align="right">
                                            {formatCurrency(valuation.valcabimp, valuation.monsim || 'S/')}
                                        </TableCell>

                                        <TableCell align="right">
                                            <Typography fontWeight="bold">
                                                {formatCurrency(valuation.valcabtot, valuation.monsim || 'S/')}
                                            </Typography>
                                        </TableCell>

                                        <TableCell>
                                            <Chip
                                                label={stateInfo.label}
                                                size="small"
                                                sx={{
                                                    backgroundColor: stateInfo.bg,
                                                    color: stateInfo.color,
                                                    fontWeight: 'bold',
                                                    minWidth: 115,
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell align="center">
                                            <Box display="flex" justifyContent="center" gap={0.5}>
                                                <Tooltip title="Ver detalle">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            navigate(`/administracion/valorizaciones/${valuation.valcabid}`)
                                                        }
                                                    >
                                                        <VisibilityIcon sx={{ color: '#009688' }} fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {canEdit(valuation) && (
                                                    <Tooltip title="Editar cabecera">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenEdit(valuation)}
                                                        >
                                                            <EditIcon color="primary" fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                <Tooltip title="Recalcular totales">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRecalculate(valuation)}
                                                        disabled={recalculateMutation.isPending}
                                                    >
                                                        <RestartAltIcon color="secondary" fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {canDelete(valuation) && (
                                                    <Tooltip title="Eliminar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDelete(valuation)}
                                                        >
                                                            <DeleteIcon color="error" fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                sx={{ mt: 2 }}
            />

            {/* Modal eliminar */}
            <Dialog
                open={openConfirmDelete}
                onClose={() => setOpenConfirmDelete(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de eliminar la valorización{' '}
                        <strong>
                            {selectedValuation
                                ? `VAL-${String(selectedValuation.valcabid).padStart(6, '0')}`
                                : ''}
                        </strong>
                        ?
                    </DialogContentText>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="body2" color="text.secondary">
                        Esta acción retirará las OS asociadas si la valorización todavía es editable.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirmDelete(false)}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 
        Aquí irán los formularios reales cuando los construyamos.
        Puedes descomentar cuando existan.
      */}


            <ValuationForm
                open={openManualForm}
                handleClose={() => {
                    setOpenManualForm(false);
                    setEditValuation(null);
                }}
                defaultValues={editValuation || {}}
                projects={projects}
                currencies={currencieTypes}
                taxes={taxesTypes}
            />

            <ValuationGenerateForm
                open={openGenerateForm}
                handleClose={() => setOpenGenerateForm(false)}
                projects={projects}
                currencies={currencieTypes}
                taxes={taxesTypes}
            />

            {/*
      {!openManualForm && !openGenerateForm ? null : (
        <Dialog
          open={openManualForm || openGenerateForm}
          onClose={() => {
            setOpenManualForm(false);
            setOpenGenerateForm(false);
            setEditValuation(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {openGenerateForm
              ? 'Generar valorización automática'
              : editValuation
                ? 'Editar valorización'
                : 'Nueva valorización'}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 1 }}>
              El formulario real lo construiremos en el siguiente paso.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenManualForm(false);
                setOpenGenerateForm(false);
                setEditValuation(null);
              }}
            >
              Cerrar
            </Button>
          </DialogActions>
        </Dialog>
      )}
      */}
        </Paper>
    );
};

export default ValuationsDashboard;